import { DEFAULT_PLATFORM_FALLBACK_URL } from '@myplayer/config';
import { canPerformCapability, type CallerIdentity } from '@myplayer/rbac';
import {
  SECTOR_TEMPLATES,
  type ApiClientDocument,
  type RetiredCodeBehaviour,
  type SectorId,
  type WorkspaceDocument,
} from '@myplayer/schema';
import { doc, serverTimestamp, updateDoc, type Firestore } from 'firebase/firestore';
import { formatWorkspaceName } from './auth-session.js';
import {
  callArchiveWorkspaceFunction,
  callCreateWorkspaceFunction,
  callUpdateWorkspaceFunction,
  isFunctionsEmulatorOrLiveBackendConfigured,
  type PartnerApiScope,
} from './firebase.js';

export const WORKSPACE_SECTOR_OPTIONS: readonly {
  readonly value: SectorId;
  readonly label: string;
}[] = (Object.keys(SECTOR_TEMPLATES) as SectorId[]).map((sectorId) => ({
  value: sectorId,
  label: SECTOR_TEMPLATES[sectorId].name,
}));

export function slugifyWorkspaceName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function deriveWorkspaceIdFromName(name: string, orgId?: string): string {
  const slug = slugifyWorkspaceName(name);
  if (slug.length === 0) {
    throw new Error('Workspace name must contain at least one alphanumeric character.');
  }
  const nameToken = slug.startsWith('ws_') ? slug.slice(3) : slug.replace(/-/g, '_');
  const orgSlug = orgId !== undefined ? slugifyWorkspaceName(orgId).replace(/-/g, '_') : '';
  if (orgSlug.length > 0) {
    return nameToken.startsWith(`${orgSlug}_`) ? `ws_${nameToken}` : `ws_${orgSlug}_${nameToken}`;
  }
  return `ws_${nameToken}`;
}

export function mapSnapshotDocToWorkspace(
  docId: string,
  data: Partial<WorkspaceDocument>,
  orgId: string,
): WorkspaceDocument {
  const sector = data.sector ?? 'RETAIL_CPG';
  const workspaceId = data.workspaceId ?? docId;
  return {
    workspaceId,
    orgId: data.orgId ?? orgId,
    name: formatWorkspaceName(data.name, workspaceId),
    slug: data.slug ?? docId,
    sector,
    enabledSectors: data.enabledSectors ?? [sector],
    status: data.status ?? 'ACTIVE',
    defaultLocale: data.defaultLocale ?? 'en-GB',
    defaultFallbackUrl: data.defaultFallbackUrl ?? DEFAULT_PLATFORM_FALLBACK_URL,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : '',
    updatedBy: data.updatedBy ?? '',
  };
}

export function assertCallerCanMutateWorkspace(
  caller: CallerIdentity,
  capability: 'workspace:create' | 'workspace:update',
  targetOrgId: string,
): void {
  if (!canPerformCapability(caller, capability, targetOrgId)) {
    throw new Error(
      `Caller (${caller.role}) in org '${caller.orgId}' is not permitted to perform '${capability}' on org '${targetOrgId}'.`,
    );
  }
}

export interface CreateWorkspaceInFirestoreParams {
  readonly db: Firestore;
  readonly caller: CallerIdentity;
  readonly orgId: string;
  readonly workspaceId?: string;
  readonly name: string;
  readonly sector?: SectorId;
  readonly defaultLocale?: string;
  readonly defaultFallbackUrl?: string;
}

/**
 * Creates a workspace document at `/organisations/{orgId}/workspaces/{workspaceId}` via
 * the `createWorkspace` callable (`allow create: if false` in `firestore.rules`).
 */
export async function createWorkspaceInFirestore(
  params: CreateWorkspaceInFirestoreParams,
): Promise<WorkspaceDocument> {
  assertCallerCanMutateWorkspace(params.caller, 'workspace:create', params.orgId);

  const trimmedName = params.name.trim();
  if (trimmedName.length === 0) {
    throw new Error('Workspace name is required.');
  }

  const workspaceId =
    params.workspaceId !== undefined && params.workspaceId.trim().length > 0
      ? params.workspaceId.trim()
      : deriveWorkspaceIdFromName(trimmedName, params.orgId);
  const slug = slugifyWorkspaceName(trimmedName) || workspaceId;
  const sector: SectorId = params.sector ?? 'RETAIL_CPG';
  const defaultLocale = (params.defaultLocale ?? 'en-GB').trim() || 'en-GB';
  const defaultFallbackUrl =
    (params.defaultFallbackUrl ?? DEFAULT_PLATFORM_FALLBACK_URL).trim() ||
    DEFAULT_PLATFORM_FALLBACK_URL;

  return callCreateWorkspaceFunction({
    orgId: params.orgId,
    workspaceId,
    name: trimmedName,
    slug,
    sector,
    enabledSectors: [sector],
    defaultLocale,
    defaultFallbackUrl,
  });
}

export interface RenameWorkspaceInFirestoreParams {
  readonly db: Firestore;
  readonly caller: CallerIdentity;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly name: string;
}

/**
 * Renames a workspace document at `/organisations/{orgId}/workspaces/{workspaceId}` directly
 * under Firestore security rules (`isOrgAdmin(orgId) && hasValidAuditStamp()`).
 */
export async function renameWorkspaceInFirestore(
  params: RenameWorkspaceInFirestoreParams,
): Promise<{ readonly workspaceId: string; readonly name: string; readonly slug: string }> {
  assertCallerCanMutateWorkspace(params.caller, 'workspace:update', params.orgId);

  const trimmedName = params.name.trim();
  if (trimmedName.length === 0) {
    throw new Error('Workspace name cannot be empty.');
  }
  const slug = slugifyWorkspaceName(trimmedName) || params.workspaceId;

  if (isFunctionsEmulatorOrLiveBackendConfigured()) {
    const res = await callUpdateWorkspaceFunction({
      orgId: params.orgId,
      workspaceId: params.workspaceId,
      name: trimmedName,
      slug,
    });
    return {
      workspaceId: res.workspaceId,
      name: res.workspace.name,
      slug: res.workspace.slug,
    };
  }

  const wsRef = doc(params.db, 'organisations', params.orgId, 'workspaces', params.workspaceId);
  await updateDoc(wsRef, {
    displayName: trimmedName,
    name: trimmedName,
    slug,
    updatedAt: serverTimestamp(),
    updatedBy: params.caller.uid,
  });

  return {
    workspaceId: params.workspaceId,
    name: trimmedName,
    slug,
  };
}

export interface ArchiveWorkspaceInFirestoreParams {
  readonly db: Firestore;
  readonly caller: CallerIdentity;
  readonly orgId: string;
  readonly workspaceId: string;
}

/**
 * Archives a workspace document (`status: 'ARCHIVED'`) via the `archiveWorkspace` callable.
 * (`status` is a server-owned field denied on direct client writes in `firestore.rules`.)
 */
export async function archiveWorkspaceInFirestore(
  params: ArchiveWorkspaceInFirestoreParams,
): Promise<{ readonly workspaceId: string; readonly status: 'ARCHIVED' }> {
  assertCallerCanMutateWorkspace(params.caller, 'workspace:update', params.orgId);

  const res = await callArchiveWorkspaceFunction({
    orgId: params.orgId,
    workspaceId: params.workspaceId,
  });
  return {
    workspaceId: res.workspaceId,
    status: res.status,
  };
}

export interface UpdateWorkspaceConfigurationParams {
  readonly db: Firestore;
  readonly caller: CallerIdentity;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly defaultFallbackUrl: string;
  readonly defaultLocale: string;
  readonly retiredCodeBehaviour: RetiredCodeBehaviour;
  readonly gs1CompanyPrefixes: readonly string[];
  readonly enforcePrefixValidation: boolean;
}

export async function updateWorkspaceConfigurationInFirestore(
  params: UpdateWorkspaceConfigurationParams,
): Promise<void> {
  if (!canPerformCapability(params.caller, 'workspace:update', params.orgId, params.workspaceId)) {
    throw new Error(
      `Caller (${params.caller.role}) in org '${params.caller.orgId}' is not permitted to perform 'workspace:update' on org '${params.orgId}'.`,
    );
  }
  const fallbackUrl = params.defaultFallbackUrl.trim();
  if (!fallbackUrl.startsWith('https://')) {
    throw new Error('Default fallback URL must start with https://');
  }
  const locale = params.defaultLocale.trim() || 'en-GB';
  const prefixes = params.gs1CompanyPrefixes
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && /^\d{4,12}$/.test(p));

  await callUpdateWorkspaceFunction({
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    defaultFallbackUrl: fallbackUrl,
    defaultLocale: locale,
    retiredCodeBehaviour: params.retiredCodeBehaviour,
    gs1CompanyPrefixes: prefixes,
    enforcePrefixValidation: params.enforcePrefixValidation,
  });
}

export const AVAILABLE_PARTNER_API_SCOPES: readonly {
  readonly value: PartnerApiScope;
  readonly label: string;
}[] = [
  { value: 'identifiers:read', label: 'View products' },
  { value: 'identifiers:write', label: 'Create & edit products' },
  { value: 'qualifiers:read', label: 'View batch & serial rules' },
  { value: 'qualifiers:write', label: 'Edit batch & serial rules' },
  { value: 'recalls:write', label: 'Activate & clear recalls' },
  { value: 'domains:read', label: 'View custom domains' },
  { value: 'domains:write', label: 'Manage custom domains' },
  { value: 'analytics:read', label: 'View scan analytics' },
  { value: 'analytics:write', label: 'Export scan analytics' },
  { value: 'webhooks:read', label: 'View webhooks' },
  { value: 'webhooks:write', label: 'Manage webhooks' },
  { value: 'myplayer:sync', label: 'Sync with MyPlayer' },
];

export function validateWorkspaceDraft(
  fallbackUrl: string,
  prefixesInput: string,
):
  | { readonly ok: true; readonly prefixes: string[] }
  | { readonly ok: false; readonly error: string } {
  const trimmedUrl = fallbackUrl.trim();
  let isValidHttps = false;
  if (trimmedUrl.startsWith('https://')) {
    try {
      const parsed = new URL(trimmedUrl);
      isValidHttps = parsed.protocol === 'https:' && parsed.hostname.includes('.');
    } catch {
      isValidHttps = false;
    }
  }
  if (!isValidHttps) {
    return {
      ok: false,
      error:
        'Please enter a valid web address starting with https:// (for example, https://example.com/fallback).',
    };
  }
  const rawEntries = prefixesInput
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  for (const entry of rawEntries) {
    if (!/^\d{4,12}$/.test(entry)) {
      return {
        ok: false,
        error:
          'Each GS1 company prefix must be 4 to 12 digits (for example, 5012345). Separate multiple prefixes with commas.',
      };
    }
  }
  return { ok: true, prefixes: rawEntries };
}

export function validateApiKeyDraft(
  keyName: string,
  selectedScopes: readonly PartnerApiScope[],
  rateLimitRpm: string,
):
  | { readonly ok: true; readonly trimmedName: string; readonly parsedRpm: number }
  | { readonly ok: false; readonly error: string } {
  const trimmedName = keyName.trim();
  if (!trimmedName) {
    return {
      ok: false,
      error: 'Please enter a name for the API key (for example, ERP Sync Key).',
    };
  }
  if (selectedScopes.length === 0) {
    return {
      ok: false,
      error: 'Please select at least one permission for the API key.',
    };
  }
  const parsedRpm = Number.parseInt(rateLimitRpm, 10);
  if (Number.isNaN(parsedRpm) || parsedRpm < 10 || parsedRpm > 60000) {
    return {
      ok: false,
      error: 'Rate limit must be between 10 and 60,000 requests per minute.',
    };
  }
  return { ok: true, trimmedName, parsedRpm };
}

export function formatScopeLabel(scope: string): string {
  return AVAILABLE_PARTNER_API_SCOPES.find((opt) => opt.value === scope)?.label ?? scope;
}

export function mapSnapshotToApiClients(
  docs: readonly { readonly id: string; data(): Partial<ApiClientDocument> }[],
  orgId: string,
  workspaceId: string,
): ApiClientDocument[] {
  return docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      clientId: data.clientId ?? docSnap.id,
      orgId: data.orgId ?? orgId,
      workspaceId: data.workspaceId ?? workspaceId,
      name: data.name ?? docSnap.id,
      keyPrefix: data.keyPrefix ?? 'mpk_live_****',
      secretHash: data.secretHash ?? '',
      scopes: data.scopes ?? ['identifiers:read'],
      rateLimitRpm: data.rateLimitRpm ?? 600,
      status: data.status ?? 'ACTIVE',
      lastUsedAt: data.lastUsedAt ?? null,
      expiresAt: data.expiresAt ?? null,
      createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
      updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : '',
      updatedBy: data.updatedBy ?? '',
    };
  });
}
